// This client was automatically generated by Segment Typewriter. ** Do Not Edit **
// To update this file, run:
//   npx typewriter

/**
 * Opened the summary panel, right before checkout
 */
export interface CustomizationsPanelOpenened {
    /**
     * This property is added as a workaround to generate type for events without properties
     */
    _?: null;
    [property: string]: any;
}

/**
 * Closed summary panel by clicking "edit configuration"
 */
export interface EditConfigurationFromCustomizationsPanel {
    /**
     * This property is added as a workaround to generate type for events without properties
     */
    _?: null;
    [property: string]: any;
}

/**
 * Enlarge frame to see the details
 */
export interface EnlargeFrame {
    /**
     * This property is added as a workaround to generate type for events without properties
     */
    _?: null;
    [property: string]: any;
}

/**
 * Entry point for the configurator
 */
export interface EnteredConfigurator {
    entry: string;
    [property: string]: any;
}

/**
 * User opened an information modal
 */
export interface InformationModalOpened {
    step: InformationModalOpenedStep;
    [property: string]: any;
}

export type InformationModalOpenedStep = "frame" | "photos" | "plate" | "shirt";

/**
 * Navigated to a step by scrolling, and stayed on it for 2 seconds
 */
export interface NavigatedToStep {
    step: NavigatedToStepStep;
    [property: string]: any;
}

export type NavigatedToStepStep = "frame" | "logo" | "photos" | "plate" | "shirt";

/**
 * Navigated to the checkout and ended the configuration
 */
export interface NavigatedToTheCheckout {
    /**
     * This property is added as a workaround to generate type for events without properties
     */
    _?: null;
    [property: string]: any;
}

/**
 * Chose an option
 */
export interface OptionChosen {
    meta?:  string;
    option: Option;
    value:  string;
    [property: string]: any;
}

export type Option = "background" | "fold" | "frame" | "logo" | "photo_center" | "photo_left" | "photo_right" | "photos" | "plate" | "plate_branding" | "plate_inscription" | "plate_logo" | "sleeves";

/**
 * Shrunk the frame after enlarging it
 */
export interface ShrinkFrame {
    /**
     * This property is added as a workaround to generate type for events without properties
     */
    _?: null;
    [property: string]: any;
}


/**
 * The analytics.js snippet should be available via window.analytics.
 * You can install it by following instructions at: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/
 * Make sure to also include the TypeScript declarations with: `npm install @segment/analytics-next` (install with --save-dev for type definitions only).
 *
 * If you don't want to use the snippet, you can also install the `@segment/analytics-next` library as a *production* dependency and use it like this: 
 * ```ts
 * import { AnalyticsBrowser } from '@segment/analytics-next' 
 * import { setTypewriterOptions } from './analytics'
 *
 * const analytics = AnalyticsBrowser.load({ writeKey: 'SEGMENT_WRITE_KEY' })
 * 
 * setTypewriterOptions({ analytics: analytics })
 */
import type { AnalyticsSnippet, Analytics, AnalyticsBrowser, Options } from '@segment/analytics-next'

declare global {
    interface Window {
        analytics: AnalyticsSnippet;
    }
}

type AnyAnalytics = AnalyticsSnippet | Analytics | AnalyticsBrowser

/** The callback exposed by analytics.js. */
export type Callback = () => void

export type ViolationHandler = (
    message: Record<string, any>,
    violations: any[]
) => void

/**
 * The default handler that is fired if none is supplied with setTypewriterOptions.
 * This handler will log a warning message to the console.
 */
export const defaultValidationErrorHandler: ViolationHandler = (message, violations) => {
    const msg = JSON.stringify(
        {
            type: 'Typewriter JSON Schema Validation Error',
            description:
                `You made an analytics call (${message.event}) using Typewriter that doesn't match the ` +
                'Tracking Plan spec.',
            errors: violations,
        },
        undefined,
        2,
    );

    console.warn(msg);
};


let analytics: () => AnyAnalytics | undefined = () => {
    return window.analytics;
};

/** Options to customize the runtime behavior of a Typewriter client. */
export interface TypewriterOptions {
    /**
     * Underlying analytics instance where analytics calls are forwarded on to.
     * Defaults to window.analytics.
     */
    analytics?: AnyAnalytics;
    /**
     * Handler fired when if an event does not match its spec. This handler
     * does not fire in production mode, because it requires inlining the full
     * JSON Schema spec for each event in your Tracking Plan.
     *
     * By default, it will throw errors if NODE_ENV = "test" so that tests will fail
     * if a message does not match the spec. Otherwise, errors will be logged to stderr.
     */
    onViolation?: ViolationHandler;
}

/**
 * Updates the run-time configuration of this Typewriter client.
 *
 * @param {TypewriterOptions} options - the options to upsert
 *
 * @typedef {Object} TypewriterOptions
 * @property {AnyAnalytics} [analytics] - Underlying analytics instance where analytics
 * 		calls are forwarded on to. Defaults to window.analytics.
 * @property {Function} [onViolation] - Handler fired when if an event does not match its spec. This handler does not fire in
 * 		production mode, because it requires inlining the full JSON Schema spec for each event in your Tracking Plan. By default,
 * 		it will throw errors if NODE_ENV="test" so that tests will fail if a message does not match the spec. Otherwise, errors
 * 		will be logged to stderr.
 */
export function setTypewriterOptions(options: TypewriterOptions) {
    analytics = options.analytics ? () => options.analytics || window.analytics : analytics;
}


/**
 * Helper to attach metadata on Typewriter to outbound requests.
 * This is used for attribution and debugging by the Segment team.
 */
function withTypewriterContext(message: Options = {}): Options {
    return {
        ...message,
        context: {
            ...(message.context || {}),
            typewriter: {
                language: 'typescript',
                version: '9.2.0',
            },
        },
    };
}

/**
 * Fires a 'CustomizationsPanelOpenened' track call.
 *
 * @param CustomizationsPanelOpenened props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function customizationsPanelOpenened(props: CustomizationsPanelOpenened, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Customizations panel openened', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'EditConfigurationFromCustomizationsPanel' track call.
 *
 * @param EditConfigurationFromCustomizationsPanel props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function editConfigurationFromCustomizationsPanel(props: EditConfigurationFromCustomizationsPanel, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Edit configuration from customizations panel', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'EnlargeFrame' track call.
 *
 * @param EnlargeFrame props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function enlargeFrame(props: EnlargeFrame, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Enlarge frame', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'EnteredConfigurator' track call.
 *
 * @param EnteredConfigurator props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function enteredConfigurator(props: EnteredConfigurator, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Entered configurator', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'InformationModalOpened' track call.
 *
 * @param InformationModalOpened props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function informationModalOpened(props: InformationModalOpened, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Information modal opened', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'NavigatedToStep' track call.
 *
 * @param NavigatedToStep props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function navigatedToStep(props: NavigatedToStep, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Navigated to step', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'NavigatedToTheCheckout' track call.
 *
 * @param NavigatedToTheCheckout props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function navigatedToTheCheckout(props: NavigatedToTheCheckout, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Navigated to the checkout', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'OptionChosen' track call.
 *
 * @param OptionChosen props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function optionChosen(props: OptionChosen, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Option chosen', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}
/**
 * Fires a 'ShrinkFrame' track call.
 *
 * @param ShrinkFrame props - The analytics properties that will be sent to Segment.
 * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
 * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
 * 	call is fired.
 */
export function shrinkFrame(props: ShrinkFrame, options?: Options, callback?: Callback): void {


    const a = analytics();
    if (a) {
        a.track('Shrink frame', props || {}, {...options,   context: {
            ...(options?.context || {}),
            typewriter: {
                language: 'typescript',
                version: '',
            },
        },}, callback);
    }
}

const clientAPI = {
    /**
     * Updates the run-time configuration of this Typewriter client.
     *
     * @param {TypewriterOptions} options - the options to upsert
     *
     * @typedef {Object} TypewriterOptions
     * @property {AnyAnalytics} [analytics] - Underlying analytics instance where analytics
     * 		calls are forwarded on to. Defaults to window.analytics.
     * @property {Function} [onViolation] - Handler fired when if an event does not match its spec. This handler does not fire in
     * 		production mode, because it requires inlining the full JSON Schema spec for each event in your Tracking Plan. By default,
     * 		it will throw errors if NODE_ENV="test" so that tests will fail if a message does not match the spec. Otherwise, errors
     * 		will be logged to stderr.
     */
    setTypewriterOptions,

    /**
     * Fires a 'CustomizationsPanelOpenened' track call.
     *
     * @param CustomizationsPanelOpenened props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    customizationsPanelOpenened,
    /**
     * Fires a 'EditConfigurationFromCustomizationsPanel' track call.
     *
     * @param EditConfigurationFromCustomizationsPanel props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    editConfigurationFromCustomizationsPanel,
    /**
     * Fires a 'EnlargeFrame' track call.
     *
     * @param EnlargeFrame props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    enlargeFrame,
    /**
     * Fires a 'EnteredConfigurator' track call.
     *
     * @param EnteredConfigurator props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    enteredConfigurator,
    /**
     * Fires a 'InformationModalOpened' track call.
     *
     * @param InformationModalOpened props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    informationModalOpened,
    /**
     * Fires a 'NavigatedToStep' track call.
     *
     * @param NavigatedToStep props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    navigatedToStep,
    /**
     * Fires a 'NavigatedToTheCheckout' track call.
     *
     * @param NavigatedToTheCheckout props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    navigatedToTheCheckout,
    /**
     * Fires a 'OptionChosen' track call.
     *
     * @param OptionChosen props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    optionChosen,
    /**
     * Fires a 'ShrinkFrame' track call.
     *
     * @param ShrinkFrame props - The analytics properties that will be sent to Segment.
     * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
     * @param {Function} [callback] - An optional callback called after a short timeout after the analytics
     * 	call is fired.
     */
    shrinkFrame,
};

export default new Proxy<typeof clientAPI>(clientAPI, {
    get(target, method) {
        if (typeof method === 'string' && target.hasOwnProperty(method)) {
            return target[method as keyof typeof clientAPI];
        }

        return () => {
            console.warn(`⚠️  You made an analytics call (${String(method)}) that can't be found. Either:
         a) Re-generate your typewriter client: \`npx typewriter\`
         b) Add it to your Tracking Plan: https://app.segment.com/segment-oscb/protocols/tracking-plans/rs_1zTHJU9fd5mt7cndWnd4PgJbMCE`);
            const a = analytics();
            if (a) {
                a.track(
                    'Unknown Analytics Call Fired',
                    {
                        method,
                    },
                    withTypewriterContext(),
                );
            }
        };
    },
});

